import { useState } from "react";
import { motion } from "framer-motion";
import { HandHelping, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const AREAS = [
  "Gate Entries",
  "Canteen Helpers",
  "Infield Helpers",
  "Video Takers",
  "Photographers",
  "I want to help anywhere, just let me know",
];

const inputCls =
  "w-full bg-clay-bg border border-clay-border focus:border-clay-gold outline-none px-4 py-3 text-sm transition-colors placeholder:text-clay-muted/50";

export default function Volunteer() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", areas: [] });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleArea = (area) =>
    setForm((f) => ({
      ...f,
      areas: f.areas.includes(area) ? f.areas.filter((a) => a !== area) : [...f.areas, area],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Name, number and email are required");
      return;
    }
    if (form.areas.length === 0) {
      toast.error("Pick at least one area you'd like to help with");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/volunteer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setDone(true);
      toast.success("Thanks for volunteering — the club will be in touch");
    } catch {
      toast.error("Couldn't send — please try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="volunteer-section" id="volunteer" className="relative bg-clay-surface/50 border-y border-clay-border/60 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-clay-gold mb-4">Get Involved</p>
          <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mb-5 leading-[0.95]">
            Volunteer<br />
            <span className="text-clay-orange">With Us</span>
          </h2>
          <p className="text-clay-muted leading-relaxed max-w-md">
            Race meetings don't happen without volunteers. We're looking for gate people, canteen helpers,
            infield helpers, video takers, photographers and more. Infield helpers need Speedway Australia
            insurance. Your help makes a huge difference.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="border border-clay-border bg-clay-card p-6 sm:p-8"
        >
          {done ? (
            <div data-testid="volunteer-success" className="text-center py-10">
              <HandHelping className="w-12 h-12 text-clay-gold mx-auto mb-4" />
              <h3 className="font-display font-extrabold uppercase text-2xl tracking-tight mb-3">You're a Legend</h3>
              <p className="text-clay-muted text-sm leading-relaxed">
                Your details are with the club — someone from the committee will be in touch before the next meeting.
                A confirmation email is on its way to you.
              </p>
            </div>
          ) : (
            <form data-testid="volunteer-form" onSubmit={submit} className="space-y-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay-muted">
                Expression of Interest — Volunteering
              </p>
              <input data-testid="volunteer-name-input" placeholder="Your name *" value={form.name} onChange={set("name")} className={inputCls} required />
              <input data-testid="volunteer-phone-input" placeholder="Phone number *" value={form.phone} onChange={set("phone")} className={inputCls} required />
              <input data-testid="volunteer-email-input" type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} required />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mb-2">
                  I'd like to help with… *
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {AREAS.map((label) => {
                    const sentence = label.startsWith("I want");
                    return (
                      <button
                        key={label}
                        type="button"
                        data-testid={`volunteer-area-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => toggleArea(label)}
                        className={`border font-display font-bold tracking-wider text-xs px-3 py-2.5 text-left transition-colors duration-300 ${
                          sentence ? "col-span-2" : "uppercase"
                        } ${
                          form.areas.includes(label)
                            ? "border-clay-orange bg-clay-orange/15 text-clay-text"
                            : "border-clay-border text-clay-muted hover:border-clay-orange/60"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                data-testid="volunteer-submit-button"
                disabled={sending}
                className="w-full bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-6 py-4 transition-colors duration-300 flex items-center justify-center gap-2"
              >
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <HandHelping className="w-5 h-5" />}
                Put My Hand Up
              </button>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted text-center">
                Goes straight to the club — no commitment, just a chat
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
